import { Link } from "react-router-dom";
import { Camera, Fingerprint, Tag, Warehouse } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const Feature = ({ icon: Icon, title, sub, code }) => (
  <div className="panel p-6 relative overflow-hidden">
    <div className="absolute top-0 left-0 h-[2px] w-16 bg-[var(--accent-red)]" />
    <div className="flex items-start justify-between mb-4">
      <div className="w-10 h-10 border border-shop-strong bg-elevated flex items-center justify-center">
        <Icon size={20} className="text-amber-brass" />
      </div>
      <span className="tag-chip">{code}</span>
    </div>
    <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-cream">{title}</h3>
    <p className="text-secondary text-sm mt-2 leading-relaxed">{sub}</p>
  </div>
);

const Landing = () => {
  const { user } = useAuth();
  return (
    <div className="space-y-16">
      <section className="relative min-h-[70vh] flex items-center overflow-hidden panel">
        <video
          data-testid="hero-video"
          src="/hero-ad.mp4"
          poster="/hero-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover opacity-45"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--bg-base)] via-[var(--bg-base)]/85 to-transparent" />
        <div className="absolute inset-0 blueprint-grid opacity-40" />

        <div className="relative z-10 px-8 md:px-14 py-16 max-w-3xl">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[var(--accent-amber)]" />
            <span className="tag-chip">Serial No. 0001 // Field Manual</span>
          </div>
          <h1 className="font-display font-extrabold uppercase tracking-tight text-cream leading-[0.9] text-5xl sm:text-6xl lg:text-7xl">
            Every Rivet.<br />
            <span className="text-red-candy">Every Digit.</span><br />
            Every Story.
          </h1>
          <p className="text-secondary text-base sm:text-lg mt-6 max-w-xl leading-relaxed">
            Point a camera at a hood, punch in a VIN, or photograph a cowl tag —
            ChromeVault decodes the vehicle&apos;s DNA and files it in your personal garage.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {user ? (
              <Link to="/identify" data-testid="hero-cta-start" className="chrome-btn">
                Open Workshop →
              </Link>
            ) : (
              <>
                <Link to="/register" data-testid="hero-cta-signup" className="chrome-btn">
                  Start Decoding
                </Link>
                <Link to="/login" data-testid="hero-cta-login" className="ghost-btn">
                  I Have Keys
                </Link>
              </>
            )}
          </div>
          <div className="mt-10 flex gap-8 font-mono text-xs text-secondary tracking-widest uppercase">
            <div><span className="text-amber-brass">01</span> — Photo ID</div>
            <div><span className="text-amber-brass">02</span> — VIN Decode</div>
            <div><span className="text-amber-brass">03</span> — Body Tag</div>
          </div>
        </div>
      </section>

      <section>
        <div className="flex items-end justify-between mb-6 border-b border-shop pb-4">
          <div>
            <p className="font-mono text-xs tracking-widest text-amber-brass uppercase">// Toolkit</p>
            <h2 className="font-display text-4xl font-bold uppercase text-cream mt-1">
              Four Ways To Know What You&apos;ve Got
            </h2>
          </div>
          <span className="font-mono text-xs text-secondary hidden md:block">CV-TK/04</span>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Feature icon={Camera} code="01" title="Photo ID"
            sub="Snap a shot of any classic — AI pins the year, make, model, trim, and body style with confidence." />
          <Feature icon={Fingerprint} code="02" title="VIN Decode"
            sub="Every character explained. NHTSA data cross-referenced with a classic-car historian AI." />
          <Feature icon={Tag} code="03" title="Body Tag"
            sub="Fisher Body cowl tags, Ford door plates, Mopar fender tags — from a photo or hand-typed codes." />
          <Feature icon={Warehouse} code="04" title="Your Garage"
            sub="Save every decode. Build a searchable digital collection of every car you've owned or admired." />
        </div>
      </section>

      <section className="panel p-8 md:p-12 relative">
        <div className="absolute top-0 right-0 h-[2px] w-24 bg-[var(--accent-amber)]" />
        <div className="grid md:grid-cols-3 gap-8">
          <div>
            <span className="tag-chip">Field Notes</span>
            <h3 className="font-display text-3xl uppercase text-cream mt-3">Built for the shop, not the showroom.</h3>
          </div>
          <div className="md:col-span-2 grid sm:grid-cols-2 gap-6 text-sm text-secondary leading-relaxed">
            <p><span className="font-mono text-amber-brass">→</span> Works on muscle, hot rods, pickups, imports, and pre-war iron. If it has wheels, we&apos;ll try.</p>
            <p><span className="font-mono text-amber-brass">→</span> NHTSA vPIC handles 1981+ VINs; AI historian fills in the pre-&apos;81 gap.</p>
            <p><span className="font-mono text-amber-brass">→</span> Tag decoder knows the difference between an RPO Z28 and a Y82 pace car.</p>
            <p><span className="font-mono text-amber-brass">→</span> Nothing you upload leaves your garage without your say-so.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
